import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import produce from 'immer';
import clsx from 'clsx';
import { UserAccountMode, HomeMember } from '@moderepo/mode-internal-apis';
import { HomeMembersTableData, HomeMembersTable, PaginationBar, useSearchParams, ITEMS_PER_PAGE_10, capitalizeAll,
    getHomeMemberRoleName, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, HomeMembersTableProps, BaseListCompField, FluxDSHomeMemberCompCtrl,
    GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchHomeMembers, userAppDataContext, selectHomeMembers } from '@moderepo/mode-data-state-user-app';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useNavigate } from 'react-router-dom';
import { SortOrder } from '@moderepo/mode-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { isMemberPreviewCompOptions, MemberPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { RouteHelper } from '../routes/RouteHelper';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';



interface HomeMemberSearchParams {
    readonly sortField: keyof HomeMember;
    readonly sortOrder: SortOrder;
}


/**
 * Because this is a container, not a page, it doesn't and shouldn't have access to the route params. Therefore, the page
 * that display this container must provide these information.
 */
interface HomeMembersListingContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly homeId: number;
    readonly itemsPerPage?: number;
}


/**
 * This is a container (SMART COMPONENT) used for displaying a list of home members. Since this is a container, it will have
 * little to no UI. All the UI will be components combined together to build the content.
 */
export const HomeMembersListingContainer: React.FC<HomeMembersListingContainerProps> = (props: HomeMembersListingContainerProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [selectedMemberId, setSelectedMemberId] = useState<number | undefined>();
    const accountMode: UserAccountMode = project ? project.userAccountMode : UserAccountMode.EMAIL;
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const homeMemberController = useMemo(() => {
        return new FluxDSHomeMemberCompCtrl(appDataDispatch);
    }, [appDataDispatch]);
    const navigate = useNavigate();


    const {
        searchParams, onItemsPerPageChange, onOtherFieldsChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<HomeMemberSearchParams>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            sortField: 'userId',
            sortOrder: SortOrder.ASC,
        },
    });

    const members = selectHomeMembers(appDataState, props.homeId);


    const sortedMembers = useMemo(() => {
        return homeMemberController.sortHomeMembers(members, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder);
    }, [homeMemberController, members, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder]);


    // Get user's homes API call returns everything so we need to filter from the frontend
    const filteredMembers = useMemo(() => {
        return homeMemberController.sliceMembers(sortedMembers, searchParams.currentPage * searchParams.itemsPerPage, searchParams.itemsPerPage);
    }, [homeMemberController, sortedMembers, searchParams.currentPage, searchParams.itemsPerPage]);



    /**
     * This useEffect used for loading home members data on component init
     */
    useEffect(() => {
        if (!members) {
            appDataDispatch(fetchHomeMembers(props.homeId)).catch(() => {
                // do nothing
            });
        }
    }, [members, props.homeId, appDataDispatch]);



    /**
     * On home member selected, go immediately to the robot details page.
     */
    const onHomeMemberSelected = useCallback((member: HomeMember) => {
        RouteHelper.getInstance(navigate).gotoUserPage({ projectId: props.projectId,
            userId   : member.userId });
    }, [navigate, props.projectId]);



    /**
     * On preview home member selected, change the selectedEntity to be the selected home member
     * If 'memberId' object is NOT specified, it will be treated as Creating New member
     */
    const onPreviewHomeMemberClicked = useCallback((memberId: number | undefined) => {
        setSelectedMemberId(memberId);

        const options: MemberPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.MEMBER_PREVIEW,
            compProps: {
                data: { projectId: props.projectId, homeId: props.homeId, memberId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.homeId, uiDispatch]);


    // This useEffect is used for checking the URL to see if there is any selectedEntity and if that selected entity is a User id.
    // if it is, automatically set it as the selectedUserId
    useEffect(() => {
        if (isMemberPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data?.memberId !== undefined) {
            setSelectedMemberId(Number(slideOutPanelOptions.compProps.data.memberId));
        } else {
            setSelectedMemberId(undefined);
        }
    }, [slideOutPanelOptions]);


    /**
     * Localize the member data before passing it to the Member Component for display
     */
    const localizeMemberData = useMemo((): readonly HomeMembersTableData[] | undefined => {
        if (filteredMembers) {
            return filteredMembers.map((member: HomeMember): HomeMembersTableData => {
                return {
                    actualValue : member,
                    displayValue: {
                        id          : member.userId.toString(),
                        name        : member.name ? member.name : capitalizeAll(trans('label.none')),
                        email       : member.email ? member.email : capitalizeAll(trans('label.none')),
                        phoneNumber : member.phoneNumber ? member.phoneNumber : capitalizeAll(trans('label.none')),
                        role        : capitalizeAll(trans(getHomeMemberRoleName(member.role))),
                        verified    : capitalizeAll(trans(member.verified ? 'label.verified' : 'label.not_verified')),
                        creationTime: getDateTimeAndElapsedTime(member.creationTime, trans, false),
                    },
                    selected: selectedMemberId === member.userId,
                };
            });
        }

        return undefined;
    }, [trans, filteredMembers, selectedMemberId]);



    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {

                if (draft.sortField === field.dataItemProp) {
                    // this column is currently being sorted, change the toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else {
                    // This column is not the current column being sorted, change it to this column and
                    draft.sortField = field.dataItemProp;
                }
            }));
        }
    }, [searchParams, onOtherFieldsChange]);


    /**
     * Create membersTablePagination data and cache it
     */
    const membersTablePaginationBar = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : members ? members.length : undefined,
                hasMoreItems    : members !== undefined && members.length > ((searchParams.currentPage + 1) * searchParams.itemsPerPage),
                texts           : {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('terms.member', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, searchParams, members, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);


    /**
     * Create membersTableBaseListCompField data and cache it
     */
    const membersTableBaseListCompField = useMemo((): HomeMembersTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label: '',      // The column doesn't need a label so we can leave it blank
                },
                id: {
                    dataItemProp: 'userId',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : members && members.length > 1,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.user_name')),
                    sortable    : members && members.length > 1,
                },
                email: {
                    dataItemProp: 'email',
                    label       : capitalizeAll(trans('label.user_email')),
                    hidden      : accountMode !== UserAccountMode.EMAIL,
                    sortable    : members && members.length > 1,
                },
                phoneNumber: {
                    dataItemProp: 'phoneNumber',
                    label       : capitalizeAll(trans('label.user_phone_number')),
                    hidden      : accountMode !== UserAccountMode.PHONE,
                },
                role: {
                    dataItemProp: 'role',
                    label       : capitalizeAll(trans('label.role')),
                    hidden      : false,
                },
                verified: {
                    dataItemProp: 'verified',
                    label       : capitalizeAll(trans('label.verified')),
                    hidden      : false,
                },
                creationTime: {
                    dataItemProp: 'creationTime',
                    label       : capitalizeAll(trans('label.date_joined')),
                    hidden      : true,
                },
            },
            onSortByField,
            currentSortedField: searchParams.otherFields ? searchParams.otherFields.sortField : undefined,
            currentSortedOrder: searchParams.otherFields ? searchParams.otherFields.sortOrder : undefined,
        };
    }, [accountMode, members, onSortByField, searchParams.otherFields, trans]);



    /**
     * On onAddHomeMemberClicked, we will unselect the current selected member if there is one selected and we will pass 'true' for createMember flag.
     */
    const onAddHomeMemberClicked = useCallback(() => {
        onPreviewHomeMemberClicked(undefined);
    }, [onPreviewHomeMemberClicked]);



    return (
        <>
            {project && (
                <HomeMembersTable
                    className={clsx(props.className)}
                    listData={localizeMemberData}
                    title={capitalizeAll(trans('terms.member', { count: 2 }))}
                    icon={trans('icons.member')}
                    customPrimaryActions={accountMode !== UserAccountMode.BYOU ? [{
                        label  : capitalizeAll(trans('label.add')),
                        icon   : 'add',
                        variant: 'contained',
                        color  : 'primary',
                        onClick: onAddHomeMemberClicked,
                    }] : undefined}
                    onListItemSelected={onHomeMemberSelected}
                    onPreviewItemClicked={(member: HomeMember) => {
                        onPreviewHomeMemberClicked(member.userId);
                    }}
                    paginationComp={membersTablePaginationBar}
                    fieldsSettings={membersTableBaseListCompField}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('terms.member', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('terms.member', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {!project && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('HomeMembersListingContainer', HomeMembersListingContainer);
